/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * CoachFeedbackSection - Displays coach feedback on challenge reports
 * - For coaches: Shows input to leave/edit feedback
 * - For students: Shows feedback from their coaches
 */

import { useState, useEffect } from 'react'
import { MessageSquare, Send, Edit2, Trash2, Loader2, CheckCircle, User } from 'lucide-react'
import coachService from '../services/coachService'
import { useAuthStore } from '../stores/authStore'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('CoachFeedbackSection')

const CoachFeedbackSection = ({ challengeId, studentId, studentName }) => {
  const { user } = useAuthStore()
  const [feedback, setFeedback] = useState(null)
  const [allFeedback, setAllFeedback] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [content, setContent] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isCoachView, setIsCoachView] = useState(false)

  // Determine if the user is viewing as the student (own report), as this student's coach, or as
  // the student's guardian (read-only). A user can be more than one of these (e.g. a guardian who
  // is also a coach), so we resolve in priority order: own report > coach of this student >
  // guardian read-only.
  useEffect(() => {
    const checkViewMode = async () => {
      if (!user || !challengeId) {
        setLoading(false)
        return
      }

      if (studentId && studentId !== user.id) {
        // Viewing someone else's report. If the viewer is a coach OF THIS student, show the coach
        // input/edit view. loadCoachFeedback returns false when they aren't this student's coach.
        if (user.isCoach && (await loadCoachFeedback())) {
          return
        }
        // Otherwise fall back to a read-only guardian view of the child's coach feedback. If the
        // viewer isn't this student's guardian either, the section simply stays hidden.
        await loadGuardianFeedback()
      } else {
        // Student viewing their own report - load all feedback from their coaches.
        setIsCoachView(false)
        await loadStudentFeedback()
      }
    }

    checkViewMode()
  }, [user, challengeId, studentId])

  // Returns true if the viewer is this student's coach (and the coach view was shown), false
  // otherwise (e.g. a 403 because they aren't this student's coach).
  const loadCoachFeedback = async () => {
    try {
      setLoading(true)
      const response = await coachService.getFeedback(studentId, challengeId)
      if (response.success && response.feedback) {
        setFeedback(response.feedback)
        setContent(response.feedback.content)
      }
      setIsCoachView(true)
      return true
    } catch (error) {
      // A 403 means the viewer is a coach in general but not THIS student's coach (e.g. a guardian
      // who is also a coach). Don't show the coach input form; let the caller try the guardian view.
      if (error.response?.status !== 403) {
        logger.error('Error loading coach feedback:', error)
      }
      setIsCoachView(false)
      return false
    } finally {
      setLoading(false)
    }
  }

  // Read-only view of the coach feedback left for the viewer's child. Hidden if the viewer is not
  // this student's guardian (the endpoint 403s, handled here without a global error).
  const loadGuardianFeedback = async () => {
    try {
      setLoading(true)
      setIsCoachView(false)
      const response = await coachService.getChildFeedback(studentId, challengeId)
      if (response.success) {
        setAllFeedback(response.feedback || [])
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        logger.error('Error loading guardian feedback:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const loadStudentFeedback = async () => {
    try {
      setLoading(true)
      // Get feedback for this specific challenge by filtering
      const response = await coachService.getMyFeedback({ limit: 100 })
      if (response.success) {
        // Filter to only show feedback for this challenge
        const challengeFeedback = response.feedback.filter((f) => f.challengeId === challengeId)
        setAllFeedback(challengeFeedback)

        // Mark feedback as read
        for (const fb of challengeFeedback) {
          if (!fb.isRead) {
            await coachService.markFeedbackAsRead(fb.id).catch(() => {})
          }
        }
      }
    } catch (error) {
      logger.error('Error loading student feedback:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!content.trim()) return

    try {
      setSaving(true)
      const response = await coachService.createFeedback(studentId, challengeId, content.trim())
      if (response.success) {
        setFeedback(response.feedback)
        setIsEditing(false)
        toast.success(feedback ? 'Feedback updated' : 'Feedback saved')
      }
    } catch (error) {
      logger.error('Error saving feedback:', error)
      toast.error(error.response?.data?.message || 'Failed to save feedback')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!feedback || !confirm('Are you sure you want to delete this feedback?')) return

    try {
      setSaving(true)
      await coachService.deleteFeedback(feedback.id)
      setFeedback(null)
      setContent('')
      setIsEditing(false)
      toast.success('Feedback deleted')
    } catch (error) {
      logger.error('Error deleting feedback:', error)
      toast.error('Failed to delete feedback')
    } finally {
      setSaving(false)
    }
  }

  // Don't render if loading or no relevant content
  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading feedback...</span>
          </div>
        </div>
      </div>
    )
  }

  // Coach view - show input form
  if (isCoachView) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Coach Feedback
            </h3>
          </div>

          {feedback && !isEditing ? (
            // Show existing feedback with edit/delete options
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {feedback.content}
                </p>
                <div className="mt-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Last updated: {new Date(feedback.updatedAt).toLocaleDateString()}</span>
                  {feedback.isRead && (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Read by student
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn btn-sm btn-secondary inline-flex items-center gap-1"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="btn btn-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 inline-flex items-center gap-1"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete
                </button>
              </div>
            </div>
          ) : (
            // Show input form
            <form onSubmit={handleSubmit} className="space-y-3">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Leave feedback for ${studentName || 'this student'}...`}
                rows={4}
                maxLength={5000}
                className="input w-full resize-none"
                disabled={saving}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {content.length}/5000 characters
                </span>
                <div className="flex gap-2">
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false)
                        setContent(feedback?.content || '')
                      }}
                      className="btn btn-sm btn-secondary"
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={saving || !content.trim()}
                    className="btn btn-sm btn-primary inline-flex items-center gap-1"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {feedback ? 'Update' : 'Send'} Feedback
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    )
  }

  // Student view - show feedback from coaches
  if (allFeedback.length === 0) {
    return null // Don't show anything if no feedback
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Coach Feedback</h3>
        </div>

        <div className="space-y-4">
          {allFeedback.map((fb) => (
            <div
              key={fb.id}
              className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fb.coach?.firstName} {fb.coach?.lastName}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(fb.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {fb.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default CoachFeedbackSection
